import React, { useMemo, useState } from 'react';
import { SVGHeart, SVGClock } from '../assets/PackageSVG';
import { AirlineLogo, formatDuration, formatMoney, formatTime, stopsLabel } from './flightPresentation';
import styles from './FlightCardNew.module.scss';

type FlightSegment = {
    carrierCode?: string;
    carrierName?: string;
    number?: string;
    departure?: { at?: string; iataCode?: string; cityName?: string };
    arrival?: { at?: string; iataCode?: string; cityName?: string };
};

type FlightItinerary = {
    duration?: string;
    segments?: FlightSegment[];
};

type FlightPrice = {
    currency?: string;
    total?: string | number;
    base?: string | number;
};

type DuffelCarrier = {
    iata_code?: string;
    name?: string;
    logo_symbol_url?: string;
};

type DuffelSegment = {
    departing_at?: string;
    arriving_at?: string;
    origin?: { iata_code?: string; city_name?: string };
    destination?: { iata_code?: string; city_name?: string };
    marketing_carrier?: DuffelCarrier;
    operating_carrier?: DuffelCarrier;
    marketing_carrier_flight_number?: string;
    operating_carrier_flight_number?: string;
};

type FlightOffer = {
    id?: string;
    itineraries?: FlightItinerary[];
    price?: FlightPrice;
    numberOfBookableSeats?: number;
    // Duffel-like fields
    slices?: Array<{
        duration?: string;
        segments?: DuffelSegment[];
    }>;
    total_amount?: string | number;
    total_currency?: string;
    base_amount?: string | number;
    // Baggage allowance, when the source provides it
    luggage?: number;
    carryOn?: number;
};

type FlightCardNewProps = {
    flight: FlightOffer;
    onBookFlight?: (flight: FlightOffer) => void;
    displayCurrency?: string;
    conversionRates?: Record<string, number>;
};

const AIRPORT_CITY_MAP: Record<string, string> = {
    JFK: 'New York',
    LAX: 'Los Angeles',
    ORD: 'Chicago',
    MIA: 'Miami',
    SFO: 'San Francisco',
    SEA: 'Seattle',
    BOS: 'Boston',
    DFW: 'Dallas',
    ATL: 'Atlanta',
    DEN: 'Denver',
    LHR: 'London',
    CDG: 'Paris',
    NRT: 'Tokyo',
    DXB: 'Dubai',
    SIN: 'Singapore',
    BCN: 'Barcelona',
    FCO: 'Rome',
    AMS: 'Amsterdam',
    IST: 'Istanbul',
    BKK: 'Bangkok',
    SYD: 'Sydney',
};

const getCityByIata = (iata?: string, fallbackCity?: string) => {
    if (!iata) return fallbackCity || 'Unknown';
    return AIRPORT_CITY_MAP[iata] || fallbackCity || iata;
};

const convertMoney = (
    value: string | number | undefined,
    fromCurrency: string | undefined,
    toCurrency: string,
    conversionRates?: Record<string, number>,
) => {
    if (!value || !fromCurrency || fromCurrency.toUpperCase() === toCurrency.toUpperCase()) {
        return null;
    }

    const amount = Number(value);
    if (Number.isNaN(amount)) return null;

    const fromRate = conversionRates?.[fromCurrency.toUpperCase()];
    const toRate = conversionRates?.[toCurrency.toUpperCase()];
    if (!fromRate || !toRate) return null;

    const converted = (amount / fromRate) * toRate;
    return formatMoney(converted, toCurrency);
};

const normalizeSegments = (flight: FlightOffer): FlightSegment[] => {
    const itinerarySegments = flight?.itineraries?.[0]?.segments;
    if (itinerarySegments?.length) {
        return itinerarySegments.map((seg) => ({
            carrierCode: seg.carrierCode,
            carrierName: seg.carrierName,
            number: seg.number,
            departure: {
                at: seg.departure?.at,
                iataCode: seg.departure?.iataCode,
                cityName: seg.departure?.cityName,
            },
            arrival: {
                at: seg.arrival?.at,
                iataCode: seg.arrival?.iataCode,
                cityName: seg.arrival?.cityName,
            },
        }));
    }

    const duffelSegments = flight?.slices?.[0]?.segments;
    if (!duffelSegments?.length) return [];

    return duffelSegments.map((seg) => ({
        carrierCode: seg.marketing_carrier?.iata_code || seg.operating_carrier?.iata_code,
        carrierName: seg.marketing_carrier?.name || seg.operating_carrier?.name,
        number: seg.marketing_carrier_flight_number || seg.operating_carrier_flight_number,
        departure: {
            at: seg.departing_at,
            iataCode: seg.origin?.iata_code,
            cityName: seg.origin?.city_name,
        },
        arrival: {
            at: seg.arriving_at,
            iataCode: seg.destination?.iata_code,
            cityName: seg.destination?.city_name,
        },
    }));
};

export const FlightCardNew: React.FC<FlightCardNewProps> = ({
    flight,
    onBookFlight,
    displayCurrency = 'USD',
    conversionRates,
}) => {
    const [saved, setSaved] = useState(false);

    const segments = useMemo(() => normalizeSegments(flight), [flight]);
    if (!segments.length) return null;

    const firstSeg = segments[0] || {};
    const lastSeg = segments[segments.length - 1] || {};
    const stops = Math.max(segments.length - 1, 0);
    const stopsText = stopsLabel(stops);

    const duration = flight?.itineraries?.[0]?.duration || flight?.slices?.[0]?.duration;
    const priceCur = flight.price?.currency || flight.total_currency || 'USD';
    const priceTotal = flight.price?.total ?? flight.total_amount ?? flight.price?.base ?? flight.base_amount;

    const displayPrice =
        convertMoney(priceTotal, priceCur, displayCurrency, conversionRates) || formatMoney(priceTotal, priceCur);
    const secondary =
        priceCur.toUpperCase() !== displayCurrency.toUpperCase() ? formatMoney(priceTotal, priceCur) : null;



    const seatsLeft = flight.numberOfBookableSeats;
    const isLowSeats = seatsLeft != null && seatsLeft <= 5;

    const flightId =
        flight.id || `${firstSeg.carrierCode || 'XX'}${firstSeg.number || '000'}-${firstSeg.departure?.at || 'na'}`;

    const carrier = firstSeg.carrierCode || 'AIR';
    const carrierName = firstSeg.carrierName || carrier;
    const route = `${firstSeg.departure?.iataCode || '---'} → ${lastSeg.arrival?.iataCode || '---'}`;
    const routeTitle = `${getCityByIata(firstSeg.departure?.iataCode, firstSeg.departure?.cityName)} → ${getCityByIata(
        lastSeg.arrival?.iataCode,
        lastSeg.arrival?.cityName,
    )}`;


    return (
        <div className={styles.flightCard}>
            {/* Airline */}
            <div className={styles.fcAirline}>
                <AirlineLogo code={firstSeg.carrierCode} name={carrierName} />
                <span className={styles.fcCarrier} title={carrierName}>{carrierName}</span>
            </div>

            {/* Times + trip meta */}
            <div className={styles.fcTrip}>
                <div className={styles.fcTimes}>
                    <span>{formatTime(firstSeg.departure?.at)}</span>
                    <span className={styles.fcDash}>–</span>
                    <span>{formatTime(lastSeg.arrival?.at)}</span>
                </div>

                <div className={styles.fcMeta}>
                    <span className={styles.fcMetaItem}>
                        <SVGClock className={styles.fcMetaIcon} aria-hidden="true" />
                        {formatDuration(duration)}
                    </span>
                    <span className={styles.fcMetaSep} aria-hidden="true" />
                    <span className={styles.fcMetaItem} title={routeTitle}>{route}</span>
                    <span className={styles.fcMetaSep} aria-hidden="true" />
                    <span className={styles.fcMetaItem}>
                        Stop:&nbsp;<span className={styles.fcStops}>{stopsText}</span>
                    </span>
                    {isLowSeats && <span className={styles.fcSeats}>{seatsLeft} left</span>}
                </div>
            </div>

            {/* Price + CTA */}
            <div className={styles.fcPriceBlock}>
                <span className={styles.fcAmount}>{displayPrice || '--'}</span>
                {secondary && <span className={styles.fcOriginal}>{secondary}</span>}
                <button className={styles.fcSelectBtn} onClick={() => onBookFlight?.(flight)}>
                    BOOK
                </button>
            </div>

            <button
                className={`${styles.fcHeart}${saved ? ` ${styles.saved}` : ''}`}
                onClick={(e) => {
                    e.stopPropagation();
                    setSaved((prev) => !prev);
                }}
                aria-label={`Save flight ${flightId}`}
            >
                <SVGHeart width="14" height="14" fill={saved ? 'currentColor' : 'none'} />
            </button>
        </div>
    );
};
